import { useCallback, useEffect, useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../store";
import ScrewDropdownProps from "../DropdownProps/ScrewDropdownProps";
import { Button } from "@mui/material";
import { updateActiveScrew } from "../../store/reducers/ScrewsReducers";
import {
  deleteScrewAction,
  getScrewsAction,
  updateExistingScrewAction,
} from "../../store/actions/ScrewsActions";
import Loader from "../Loader";
import SystemMsg from "../SystemMsg";
import { ScrewsDataTypes } from "../../types/ScrewTypes";
import { SuccessCheckmarkIcon } from "../../Icons/Icons";
import ScrewGridNavigation from "./ScrewGridNavigation";
import GridPagination from "./GridPagination";
import { useParams } from "react-router-dom";
import {
  getActiveScrew,
  getScrewData,
  getScrewFetchStatus,
  getScrewFilters,
  getScrewUpdateStatus,
  getScrewsAmount,
  getScrewProperties,
} from "../../store/selectors/ScrewSelectors";
import { api } from "../../services/Api";

const ITEMS_PER_PAGE: number = 40;

const ScrewsGrid = () => {
  const dispatch = useAppDispatch();
  const { page } = useParams();
  const isFetching = useRef(false);

  const screwFetchStatus = useAppSelector(getScrewFetchStatus);
  const screwUpdateStatus = useAppSelector(getScrewUpdateStatus);
  const screwProperties = useAppSelector(getScrewProperties);

  const screwData = useAppSelector(getScrewData);
  const screws = screwData || [];
  const totalScrews = useAppSelector(getScrewsAmount);
  const activeScrew = useAppSelector(getActiveScrew);
  const screwFilters = useAppSelector(getScrewFilters);
  const isFetchFailed = screwFetchStatus === "error";
  const isScrewsLoading = screwFetchStatus === "loading";

  const [screwDraft, setScrewDraft] = useState<ScrewsDataTypes | null>(
    activeScrew
  );
  const [isUpdateDisabled, setIsUpdateDisabled] = useState(true);

  useEffect(() => {
    if (!screwDraft || !activeScrew) {
      setIsUpdateDisabled(true);
      return;
    }

    setIsUpdateDisabled(
      activeScrew.seating === screwDraft.seating &&
      activeScrew.color === screwDraft.color &&
      activeScrew.driver === screwDraft.driver &&
      activeScrew.head_diameter === screwDraft.head_diameter &&
      activeScrew.thread === screwDraft.thread &&
      activeScrew.total_length === screwDraft.total_length &&
      activeScrew.platform === screwDraft.platform &&
      activeScrew.brand === screwDraft.brand &&
      activeScrew.ref === screwDraft.ref
    );
  }, [screwDraft, activeScrew]);

  // Effect for fetching data
  useEffect(() => {
    const fetchData = async () => {
      if (isFetching.current) return;
      
      isFetching.current = true;
      try {
        // Reset active screw only on page/filter changes
        dispatch(updateActiveScrew(null));
        
        await dispatch(
          getScrewsAction({
            skip: (Number(page) - 1) * ITEMS_PER_PAGE,
            limit: ITEMS_PER_PAGE,
          })
        );
      } finally {
        // Reset the fetching flag after a short delay to prevent immediate re-fetches
        setTimeout(() => {
          isFetching.current = false;
        }, 100);
      }
    };

    fetchData();
  }, [screwFilters, page, dispatch]);

  useEffect(() => {
    setScrewDraft(activeScrew);
  }, [activeScrew]);

  const onUpdateScrewProps = useCallback((props: any) => {
    setScrewDraft((prev) => ({ ...prev, ...props }));
  }, []);

  const onSaveEditedScrew = useCallback(() => {
    if (!screwDraft) return;

    dispatch(updateExistingScrewAction(screwDraft));
  }, [dispatch, screwDraft]);

  const onDeleteScrew = useCallback(() => {
    if (activeScrew?.sampleid) {
      dispatch(deleteScrewAction(activeScrew.sampleid));
    } else {
      console.warn("No active screw to delete");
    }
  }, [activeScrew, dispatch]);

  const onCancelEditingScrew = useCallback(() => {
    dispatch(updateActiveScrew(null));
    setScrewDraft(null);
  }, [dispatch]);

  const renderScrewItem = (screw: ScrewsDataTypes) => {
    const { sampleid, imagepath, propertyname } = screw;
    const activeClassName =
      sampleid === activeScrew?.sampleid ? "active" : "";

    const handleScrewClick = () => {
      if (sampleid !== activeScrew?.sampleid) {
        api
          .get(`/datasets/screws/samples/${sampleid}/propertyvalues`)
          .then((response: { items: Array<{ propertyname: string; categoricalvalue: string }> }) => {
            const propertyValues: Partial<ScrewsDataTypes> = {};
            response.items.forEach((item) => {
              propertyValues[item.propertyname as keyof ScrewsDataTypes] = item.categoricalvalue;
            });
            
            dispatch(updateActiveScrew({
              ...screw,
              ...propertyValues
            }));
          })
          .catch((error: Error) => {
            console.error("Failed to fetch screw property values:", error);
          });
      }
    };

    return (
      <div
        className={`screw-cell ${activeClassName}`}
        key={sampleid}
        onClick={handleScrewClick}
      >
        {activeClassName && (
          <span className="checkmark">
            <SuccessCheckmarkIcon />
          </span>
        )}
        <img src={imagepath} alt={propertyname} />
      </div>
    );
  };

  const screwItems =
    screws.length === 0 ? (
      <SystemMsg message="No data with selected filters" messageType="error" />
    ) : (
      screws.map((screw) => renderScrewItem(screw))
    );

  const renderScrewDetails = useCallback(() => {
    if (!activeScrew) return <></>;

    // Get the screw properties and sort them by islabel
    const properties = screwProperties?.items || [];
    const labelProperties = properties.filter(prop => prop.islabel);
    const nonLabelProperties = properties.filter(prop => !prop.islabel);

    // Sort non-label properties: brand and platform first, then rest alphabetically
    const sortedNonLabelProperties = nonLabelProperties.sort((a, b) => {
      const aName = a.propertyname.toLowerCase();
      const bName = b.propertyname.toLowerCase();

      // If either is brand or platform, they come first
      if (aName === 'brand') return -1;
      if (bName === 'brand') return 1;
      if (aName === 'platform') return -1;
      if (bName === 'platform') return 1;

      // Otherwise sort alphabetically
      return aName.localeCompare(bName);
    });

    return (
      <>
        <h2>Data labels</h2>
        <div className="screw-details">
          {/* Render label properties first */}
          {labelProperties.map(prop => (
            <ScrewDropdownProps
              key={prop.propertyid}
              onChooseScrewProperty={(evt: any) => {
                onUpdateScrewProps({ [prop.propertyname]: evt.target.value || undefined });
              }}
              selectedScrewProperty={screwDraft?.[prop.propertyname as keyof typeof screwDraft]}
              propertyType={prop.propertyname}
            />
          ))}
          
          {/* Add a separator if we have both types */}
          {labelProperties.length > 0 && sortedNonLabelProperties.length > 0 && (
            <div className="property-separator">
              <hr />
              <span>Metadata</span>
            </div>
          )}

          {/* Render non-label properties */}
          {sortedNonLabelProperties.map(prop => (
            <ScrewDropdownProps
              key={prop.propertyid}
              onChooseScrewProperty={(evt: any) => {
                onUpdateScrewProps({ [prop.propertyname]: evt.target.value || undefined });
              }}
              selectedScrewProperty={screwDraft?.[prop.propertyname as keyof typeof screwDraft]}
              propertyType={prop.propertyname}
            />
          ))}
        </div>
        <div className="screw-actions">
          <div className="row">
            <Button
              className="btn btn-blue"
              variant="contained"
              disabled={isUpdateDisabled}
              onClick={onSaveEditedScrew}
            >
              Save changes
            </Button>
          </div>
          <div className="row">
            <Button
              className="btn btn-danger"
              variant="contained"
              onClick={onDeleteScrew}
            >
              Delete
            </Button>
          </div>
          <div className="row">
            <Button
              className="btn btn-outlined"
              variant="outlined"
              onClick={onCancelEditingScrew}
            >
              Cancel
            </Button>
          </div>
        </div>
      </>
    );
  }, [
    activeScrew,
    isUpdateDisabled,
    screwDraft,
    screwProperties,
    onCancelEditingScrew,
    onUpdateScrewProps,
    onSaveEditedScrew,
    onDeleteScrew,
  ]);

  const screwDetailsContent =
    screwUpdateStatus === "loading" ? (
      <Loader />
    ) : screwUpdateStatus === "error" ? (
      <SystemMsg />
    ) : screwUpdateStatus === "success" ? (
      <SystemMsg messageType="success" message="Success" />
    ) : (
      renderScrewDetails()
    );

  const screwDataGrid = isFetchFailed ? (
    <SystemMsg />
  ) : (
    <div className="screws-grid">
      <div className="left-side">
        <ScrewGridNavigation />
        <div className="grid">{screwItems}</div>
        <GridPagination totalItems={totalScrews} baseRoute="/dataset/screws" />
      </div>
      <div className="right-side">{screwDetailsContent}</div>
    </div>
  );

  return isScrewsLoading ? (
    <Loader />
  ) : isFetchFailed ? (
    <SystemMsg messageType="error" message="Failed to load screws data" />
  ) : screwData ? (
    screwDataGrid
  ) : (
    <SystemMsg messageType="error" message="No screws data available" />
  );
};

export default ScrewsGrid;
